// Maps the backend's ranking `stage` string to a user-facing pipeline step.
//
// The backend reports internal stage names ("Text Extraction", "AI Parsing",
// "Ranking", "Organizing"). Users don't think in those terms, and — because each
// stage is a long, opaque LLM call — a raw percentage sits still for long
// stretches and reads as "stuck". So the UI tells progress as a 4-step story
// (which step is running, which are done) plus a live shimmer/timer, and treats
// the number as a secondary magnitude cue. This helper is the single source of
// truth for that mapping, shared by the header banner and the upload page.

// Ordered pipeline steps shown in the stepper. `title` is what the user reads;
// `hint` is a one-line, plain-language description of what's happening.
export const RANKING_STEPS = [
    { key: "extract", title: "Reading resumes", hint: "Extracting text from your files" },
    { key: "parse", title: "Understanding candidates", hint: "Reading each resume with AI" },
    { key: "score", title: "Scoring candidates", hint: "Matching each candidate to your job description" },
    { key: "finalize", title: "Finalizing", hint: "Sorting and saving your results" },
];

/**
 * Resolve a backend stage string to { step, title, hint, paused }.
 * `step` is the 0-based index into RANKING_STEPS. Unknown/early stages resolve to
 * step 0 so the UI always has a sensible current step.
 */
export function getStageMeta(stage) {
    const raw = String(stage || "");
    const paused = /^paused/i.test(raw.trim());
    // A paused status arrives as "Paused: <stage>"; resolve the underlying stage.
    const s = raw.replace(/^paused\s*[:—-]?\s*/i, "").toLowerCase();

    let step = 0;
    if (s.includes("extract")) step = 0;
    else if (s.includes("pars")) step = 1;
    else if (s.includes("rank") || s.includes("scor")) step = 2;
    else if (s.includes("organiz") || s.includes("final")) step = 3;
    else if (s.includes("complete")) step = 3;
    else step = 0; // "starting", "resuming", "processing", unknown

    const meta = RANKING_STEPS[step];
    return { step, title: meta.title, hint: meta.hint, paused };
}

/** Format elapsed seconds as m:ss (e.g. 72 -> "1:12"). */
export function formatElapsed(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
}
